import { ArrowDown } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1776514222617-cd3d0139c926?crop=entropy&cs=srgb&fm=jpg&w=1600&q=50&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwY29uY3JldGUlMjB0ZXh0dXJlJTIwZGFya3xlbnwwfHx8fDE3Nzg0NjE2NzN8MA&ixlib=rb-4.1.0";

export default function Hero() {
  return (
    <section
      className="relative overflow-hidden border-b border-zinc-800"
      data-testid="hero-section"
    >
      {/* faded logo watermark behind everything (atmospheric) */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06] pointer-events-none flex items-center justify-end"
      >
        <img
          src="/drr-logo.png"
          alt=""
          className="h-[120%] w-auto object-cover"
          style={{ filter: "blur(2px) grayscale(40%)" }}
        />
      </div>
      <div
        aria-hidden
        className="absolute inset-0 opacity-15"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "grayscale(100%) contrast(1.2)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(9,9,11,0.4) 0%, rgba(9,9,11,0.85) 60%, rgba(9,9,11,1) 100%)",
        }}
      />

      <div className="relative grid grid-cols-12 items-end min-h-[55vh] md:min-h-[60vh] px-4 md:px-10 pt-10 pb-8 gap-6">
        <div className="col-span-12 md:col-span-7 min-w-0">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <span className="text-xs tracking-[0.4em] text-zinc-300 uppercase">
              //_DIGITAL_REVOLUTION_RADIO
            </span>
            <span className="h-px w-16 bg-red-600 hidden sm:inline-block" />
            <span className="text-xs tracking-[0.4em] text-red-600 uppercase">
              24 / 7 / 666
            </span>
          </div>

          <h1
            className="font-display text-[11vw] md:text-[7vw] lg:text-[6vw] leading-[0.85] tracking-tighter text-red-600 glitch select-none"
            data-testid="hero-headline"
          >
            DIGITAL REVOLUTION
            <br />
            RADIO<span className="text-red-600">.</span>
          </h1>

          <div
            className="mt-4 font-display text-2xl md:text-3xl lg:text-4xl tracking-tight text-zinc-300"
            data-testid="hero-signature"
          >
            YOU HEAR IT HERE{" "}
            <span className="text-red-600">"LOUD AND CLEAR"</span>
          </div>

          <div
            className="mt-5 font-display text-lg md:text-xl tracking-widest uppercase text-zinc-300"
            data-testid="hero-subline"
          >
            // Wired for the rock in you.
          </div>

          <p
            className="mt-5 max-w-2xl text-sm md:text-base text-zinc-50 leading-relaxed"
            data-testid="hero-tagline"
          >
            Digital Revolution Radio broadcasts hard rock and heavy metal at
            full volume — thrash, doom, death, classic, sludge. No DJs reading
            ads. No algorithm. Just signal, distortion, and the people
            screaming into the chat.
          </p>

          <div className="mt-10 flex flex-wrap gap-0 border border-zinc-700 w-fit">
            <a
              href="#listen"
              className="font-display tracking-widest uppercase text-base px-8 py-4 bg-red-600 text-white hover:bg-white hover:text-black transition-colors border-r border-zinc-700"
              data-testid="hero-tune-in-btn"
            >
              ▶ Tune In
            </a>
            <a
              href="#chat"
              className="font-display tracking-widest uppercase text-base px-8 py-4 bg-transparent text-zinc-100 hover:bg-zinc-100 hover:text-black transition-colors"
              data-testid="hero-chat-btn"
            >
              Enter the Pit
            </a>
          </div>

          {/* Mobile App download badges */}
          <div className="mt-8" data-testid="hero-app-badges">
            <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-3 flex items-center gap-3">
              <span className="h-px w-8 bg-red-600" />
              //_TAKE_US_WITH_YOU
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://apps.apple.com/us/app/digital-revolution-radio-live/id1642175296"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 bg-black border-2 border-zinc-700 hover:border-red-600 px-5 py-3 transition-colors"
                data-testid="app-store-btn"
                aria-label="Download Digital Revolution Radio on the App Store"
              >
                <svg className="w-7 h-7 text-zinc-100 group-hover:text-red-600 transition-colors" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M17.05 12.04c-.03-2.93 2.4-4.35 2.51-4.42-1.37-2-3.5-2.27-4.25-2.3-1.81-.18-3.54 1.07-4.46 1.07-.94 0-2.35-1.05-3.87-1.02-1.99.03-3.83 1.16-4.86 2.94-2.07 3.6-.53 8.92 1.48 11.84.98 1.43 2.15 3.03 3.67 2.97 1.48-.06 2.04-.96 3.82-.96 1.78 0 2.28.96 3.84.93 1.59-.03 2.59-1.45 3.55-2.89 1.13-1.66 1.59-3.27 1.61-3.36-.04-.01-3.09-1.18-3.12-4.7M14.36 3.41c.81-.99 1.36-2.36 1.2-3.72-1.17.05-2.58.78-3.42 1.76-.75.87-1.41 2.26-1.23 3.6 1.3.1 2.64-.66 3.45-1.64"/>
                </svg>
                <div className="leading-tight">
                  <div className="text-[9px] tracking-[0.3em] text-zinc-300 uppercase">Download on the</div>
                  <div className="font-display text-base tracking-tight text-zinc-50 group-hover:text-white">App Store</div>
                </div>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.digitalrevolution.radio&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 bg-black border-2 border-zinc-700 hover:border-red-600 px-5 py-3 transition-colors"
                data-testid="play-store-btn"
                aria-label="Get Digital Revolution Radio on Google Play"
              >
                <svg className="w-7 h-7 text-zinc-100 group-hover:text-red-600 transition-colors" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M3.609 1.814L13.792 12 3.609 22.186a.996.996 0 0 1-.61-.92V2.734a.996.996 0 0 1 .61-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.207 12l2.491-2.491zM5.864 2.658L16.802 8.99l-2.302 2.302-8.636-8.634z"/>
                </svg>
                <div className="leading-tight">
                  <div className="text-[9px] tracking-[0.3em] text-zinc-300 uppercase">Get it on</div>
                  <div className="font-display text-base tracking-tight text-zinc-50 group-hover:text-white">Google Play</div>
                </div>
              </a>
            </div>
          </div>
        </div>

        <div className="hidden md:flex col-span-5 flex-col items-end justify-end h-full pb-2">
          <a
            href="https://www.facebook.com/digitalrevolutionradio"
            target="_blank"
            rel="noopener noreferrer"
            className="relative w-full aspect-square border-2 border-zinc-800 overflow-hidden group img-glitch crt-scan block hover:border-red-600 transition-colors"
            data-testid="hero-logo-wrapper"
            aria-label="Digital Revolution Radio on Facebook"
            title="Visit us on Facebook"
          >
            <img
              src="/drr-logo.png"
              alt="Digital Revolution Radio"
              className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105 bg-black"
              data-testid="hero-logo"
            />
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(180deg, transparent 0%, transparent 70%, rgba(9,9,11,0.55) 100%)",
              }}
            />
            <div className="absolute top-2 left-2 text-[10px] tracking-widest uppercase bg-black/80 px-2 py-1 text-red-500 z-10">
              //_OFFICIAL_LOGO
            </div>
            <div className="absolute bottom-2 right-2 text-[10px] tracking-widest uppercase bg-red-600 text-black px-2 py-1 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              ▶ FACEBOOK →
            </div>
          </a>
          <div className="mt-3 text-xs tracking-widest text-zinc-300 uppercase">
            <ArrowDown className="inline w-3 h-3 mr-1" /> Scroll
          </div>
        </div>
      </div>
    </section>
  );
}
